"use client";

import { RequestInfo } from "rwsdk/worker";
import { AppLayout } from "@/components/layouts";

export function Terms({ ctx }: RequestInfo) {
  return (
    <AppLayout ctx={ctx}>
      <div className="container mx-auto px-4 py-12 sm:py-16 max-w-3xl">
        <div className="mb-10">
          <h1 className="font-display text-3xl sm:text-4xl tracking-tight mb-2">Terms of Service</h1>
          <p className="text-sm text-muted-foreground">Last updated: December 2024</p>
        </div>

        <div className="space-y-6">
          <Section title="Agreement to Terms">
            <p>
              By accessing and using ForwardCents, you agree to be bound by these Terms of Service and the Privacy Policy. If you do not agree, please do not use this service.
            </p>
          </Section>

          <Section title="Description of Service">
            <p>ForwardCents is an expense tracking service that:</p>
            <ul>
              <li>Provides you with a unique email address for forwarding receipts</li>
              <li>Uses AI to extract expense data from forwarded emails</li>
              <li>Organizes your expenses in a dashboard interface</li>
              <li>Allows you to edit and manage your expense data</li>
            </ul>
          </Section>

          <Section title="User Accounts and Responsibilities">
            <h4>Account Creation</h4>
            <ul>
              <li>You must provide accurate information when creating your account</li>
              <li>You are responsible for maintaining the security of your account</li>
              <li>You must be at least 18 years old to use our service</li>
            </ul>

            <h4>Acceptable Use</h4>
            <p>You agree to use ForwardCents only for legitimate expense tracking. You may not:</p>
            <ul>
              <li>Forward emails that contain illegal or harmful content</li>
              <li>Attempt to reverse engineer or compromise our service</li>
              <li>Use the service to spam or send unsolicited communications</li>
              <li>Share your unique email address publicly or use it for non-expense purposes</li>
            </ul>
          </Section>

          <Section title="Privacy and Data Processing">
            <ul>
              <li>You grant permission to process emails you forward to your ForwardCents address</li>
              <li>Your data will be handled according to our Privacy Policy</li>
              <li>You retain ownership of your expense data</li>
              <li>You can delete your data or account at any time</li>
            </ul>
          </Section>

          <Section title="Service Availability">
            <ul>
              <li>We strive for high availability but cannot guarantee 100% uptime</li>
              <li>Maintenance may temporarily interrupt service</li>
              <li>Features may be modified or discontinued with reasonable notice</li>
            </ul>
          </Section>

          <Section title="AI and Data Accuracy">
            <ul>
              <li>AI extraction is designed to be helpful but may not be 100% accurate</li>
              <li>You are responsible for reviewing and correcting extracted data</li>
              <li>The service is not liable for decisions based on AI-extracted information</li>
              <li>Always verify important financial data independently</li>
            </ul>
          </Section>

          <Section title="Pricing and Payment">
            <ul>
              <li>Both free and paid plans are offered as described on the website</li>
              <li>Pricing may change with reasonable advance notice</li>
              <li>Refunds are handled on a case-by-case basis</li>
              <li>You can cancel or downgrade your plan at any time</li>
            </ul>
          </Section>

          <Section title="Intellectual Property">
            <ul>
              <li>ForwardCents and its technology are protected intellectual property</li>
              <li>You retain rights to your original expense data</li>
              <li>You grant a license to process your data to provide the service</li>
              <li>You may not copy, modify, or redistribute this service</li>
            </ul>
          </Section>

          <Section title="Limitation of Liability">
            <p>
              ForwardCents is provided "as is" without warranties. To the maximum extent permitted by law, the service is not liable for:
            </p>
            <ul>
              <li>Indirect, incidental, or consequential damages</li>
              <li>Lost profits or business opportunities</li>
              <li>Data loss (though measures are taken to prevent it)</li>
              <li>Service interruptions or downtime</li>
            </ul>
            <p>
              Total liability shall not exceed the amount you paid in the 12 months preceding the claim.
            </p>
          </Section>

          <Section title="Termination">
            <ul>
              <li>You may terminate your account at any time</li>
              <li>Accounts that violate these terms may be terminated</li>
              <li>Upon termination, data will be deleted per the Privacy Policy</li>
              <li>These terms survive termination where necessary</li>
            </ul>
          </Section>

          <Section title="Changes to Terms">
            <p>
              These terms may be updated from time to time. You will be notified of material changes. Continued use after changes take effect constitutes acceptance.
            </p>
          </Section>

          <Section title="Contact and Disputes">
            <p>For questions about these terms or to report violations:</p>
            <ul>
              <li>Email: <a href="mailto:hey@harshil.dev" className="underline">hey@harshil.dev</a></li>
              <li>Response time: Within 48 hours</li>
            </ul>
            <p>
              These terms are governed by applicable law and any disputes will be resolved through binding arbitration.
            </p>
          </Section>
        </div>
      </div>
    </AppLayout>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="border rounded-xl p-5">
      <h2 className="text-base font-medium mb-3">{title}</h2>
      <div className="text-sm text-muted-foreground [&_h4]:text-foreground [&_h4]:text-sm [&_h4]:font-medium [&_h4]:mt-4 [&_h4]:mb-2 [&_ul]:space-y-1.5 [&_ul]:text-sm [&_ul]:list-disc [&_ul]:pl-4 [&_p]:text-sm [&_p]:leading-relaxed [&_p]:mb-2.5 [&_strong]:text-foreground">
        {children}
      </div>
    </div>
  );
}
